import type {
  CreateableBodyComponent,
  CreateableFooterComponent,
  CreateableTemplateComponent,
  MessageTemplate,
  WhatsappButtonComponent
} from '../services/AccountService/types'

interface CatalogTemplateInput {
  name: string
  language: string
  category: string
  components: CreateableTemplateComponent[]
}

export function isCatalogTemplate (template: Pick<MessageTemplate, 'components'>): boolean {
  return template.components.some(component =>
    component.type === 'BUTTONS' && 'buttons' in component &&
    component.buttons.some(button => button.type === 'CATALOG')
  )
}

export function createCatalogComponents (
  body: CreateableBodyComponent,
  footer?: CreateableFooterComponent
): CreateableTemplateComponent[] {
  return [
    body,
    ...(footer?.text.trim() ? [footer] : []),
    { type: 'BUTTONS', buttons: [{ type: 'CATALOG', text: 'View catalog' }] }
  ]
}

export function catalogTemplateError (template: CatalogTemplateInput): string | undefined {
  if (!template.name.trim() || template.name.length > 512 || !/^[a-z0-9_]+$/.test(template.name)) {
    return 'Use a name of up to 512 lowercase letters, numbers, or underscores.'
  }
  if (!template.language) return 'Select a language.'
  if (template.category !== 'MARKETING') return 'Catalog templates require the Marketing category.'
  if (template.components.some(component => component.type === 'HEADER')) {
    return 'Catalog templates use the product thumbnail instead of a custom header.'
  }
  const bodies = template.components.filter(component => component.type === 'BODY')
  const body = bodies[0] as CreateableBodyComponent | undefined
  if (bodies.length !== 1 || !body?.text.trim()) return 'Enter the message body.'
  if (body.text.length > 1024) return 'The message body allows up to 1024 characters.'
  if (/\{\{(?![1-9]\d*\}\})/.test(body.text)) {
    return 'Use sequential positional body variables: {{1}}, {{2}}, and so on.'
  }
  const indexes = [...new Set(Array.from(body.text.matchAll(/\{\{(\d+)\}\}/g), match => Number(match[1])))].sort((a, b) => a - b)
  if (indexes.some((index, position) => index !== position + 1)) {
    return 'Body variables must be sequential: {{1}}, {{2}}, and so on.'
  }
  const examples = body.example?.body_text[0] || []
  if (indexes.length && (examples.length !== indexes.length || examples.some(value => !value.trim() || value.length > 1024))) {
    return 'Provide an example of up to 1024 characters for every body variable.'
  }
  const footers = template.components.filter(component => component.type === 'FOOTER') as CreateableFooterComponent[]
  if (footers.some(footer => footer.text.length > 60)) return 'The footer allows up to 60 characters.'
  const buttons = template.components.filter(component => component.type === 'BUTTONS' && 'buttons' in component)
  if (buttons.length !== 1 || !('buttons' in buttons[0]) || buttons[0].buttons.length !== 1 ||
      buttons[0].buttons[0].type !== 'CATALOG' || buttons[0].buttons[0].text !== 'View catalog') {
    return 'Catalog templates require a single View catalog button.'
  }
}

export function catalogSendComponent (index: string, thumbnailSku = ''): WhatsappButtonComponent {
  const component: WhatsappButtonComponent = { type: 'button', sub_type: 'CATALOG', index }
  const sku = thumbnailSku.trim()
  if (sku) {
    component.parameters = [{ type: 'action', action: { thumbnail_product_retailer_id: sku } }]
  }
  return component
}
